import { useState } from 'react';
import { Switch, Tooltip, Spin, Alert, message } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import * as claus from 'src/_shared/services/claus/endpoints';
import Section from '../../console/Section.jsx';
import WebhookFormDialog from '../dialogs/WebhookFormDialog.jsx';

// Webhooks registered on this integration (claraty POSTs normalized output to them). The list itself
// (`hooks`) is loaded by the host editor so the Steps overview shares it; the action button sits on
// the section-title row per the design.
export default function WebhooksSection({ orgId, integrationId, hooks }) {
	const { loading, data, error, reload } = hooks;
	const [editing, setEditing] = useState(null); // { webhook } | { name } for new

	const items = data?.webhooks || (Array.isArray(data) ? data : []);

	async function toggle(w, enabled) {
		try {
			await claus.patchWebhook(orgId, integrationId, w.id, { enabled });
			await reload();
		} catch (e) {
			message.error(e.message || 'Could not update the webhook.');
		}
	}

	return (
		<Section
			id="sec-webhooks"
			title="Webhooks"
			description="Notify downstream services whenever a sync runs."
			actions={
				<button type="button" className="js-btn" onClick={() => setEditing({})}>
					<PlusOutlined /> Add webhook
				</button>
			}
		>
			{error && <Alert type="error" showIcon message="Could not load webhooks" description={error.message} />}

			{loading ? (
				<div className="js-loading js-loading--inline">
					<Spin />
				</div>
			) : items.length === 0 ? (
				<div className="ei-empty-inline">No webhooks registered.</div>
			) : (
				<div>
					{items.map((w) => (
						<div className="ei-hookrow" key={w.id}>
							<div className="ei-hookmain">
								<div className="ei-hookname">{w.name}</div>
								<div className="ei-hookurl" title={w.endpoint}>
									{w.endpoint}
								</div>
							</div>
							<Tooltip title={w.enabled ? 'Enabled' : 'Disabled'}>
								<Switch checked={w.enabled} onChange={(v) => toggle(w, v)} />
							</Tooltip>
							<Tooltip title="Edit">
								<button
									type="button"
									className="js-iconbtn"
									onClick={() => setEditing({ webhook: w })}
									aria-label="Edit webhook"
								>
									<EditOutlined />
								</button>
							</Tooltip>
						</div>
					))}
				</div>
			)}

			<WebhookFormDialog
				orgId={orgId}
				integrationId={integrationId}
				open={!!editing}
				webhook={editing?.webhook}
				defaultName={editing?.name}
				onClose={() => setEditing(null)}
				onSaved={async () => {
					setEditing(null);
					await reload();
				}}
			/>
		</Section>
	);
}
